import { useState } from 'react'
import BookingModal from '../components/BookingModal'
import FontAwesomeIcon from '../components/FontAwesomeIcon'
import { Link } from 'react-router-dom'

export default function MurchisonFalls() {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedExperience, setSelectedExperience] = useState('')

  const handleBooking = (experience) => {
    setSelectedExperience(experience)
    setIsModalOpen(true)
  }

  return (
    <div>
      {/* Hero Section */}
      <section
        className="relative h-96 bg-cover bg-center flex items-center"
        style={{
          backgroundImage: 'linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.5)), url(/images/murchison-falls-hero.jpg)',
        }}
      >
        <div className="container mx-auto px-4 text-center text-white">
          <h1 className="text-3xl md:text-5xl font-bold mb-3 font-heading">
            Murchison Falls National Park
          </h1>
          <p className="text-base md:text-lg max-w-2xl mx-auto">
            Where Nature Performs
          </p>
        </div>
      </section>

      {/* Overview */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-2xl font-bold text-safari-green mb-4">Destination Overview</h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Murchison Falls National Park stands as Uganda's crown jewel of safari destinations, offering an
                extraordinary blend of raw wilderness, thunderous waterfalls, and spectacular wildlife viewing
                opportunities. Uganda's largest and oldest national park spans an impressive 3,893 square kilometers,
                making it a pristine wilderness twice the size of many European countries.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Located in northwestern Uganda at the northern end of the Albertine Rift Valley, the park is bisected
                by the mighty Victoria Nile River, which creates its most spectacular attraction - the thunderous
                Murchison Falls. Here, the Nile explodes through a narrow 7-8 meter gap in the rock escarpment and
                plunges 45 meters into the "Devil's Cauldron" below, creating one of the world's most powerful waterfalls.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-bold text-safari-green mb-2">Size</h3>
                  <p className="text-gray-700">3,893 km²</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-bold text-safari-green mb-2">Mammal Species</h3>
                  <p className="text-gray-700">144 species</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-bold text-safari-green mb-2">Bird Species</h3>
                  <p className="text-gray-700">556+ species</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-bold text-safari-green mb-2">Elephants</h3>
                  <p className="text-gray-700">15,000+ individuals</p>
                </div>
              </div>
            </div>
            <div>
              <img
                src="/images/murchison-falls-hero.jpg"
                alt="Murchison Falls National Park"
                className="w-full h-96 object-cover rounded-lg shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Key Highlights */}
      <section className="py-16 bg-safari-light">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-2xl font-bold text-safari-green mb-8 text-center">Key Highlights</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-lg">
              <h3 className="text-lg font-bold text-safari-green mb-2">The Spectacular Murchison Falls</h3>
              <p className="text-gray-700">
                The park's namesake waterfall represents one of nature's most powerful displays, where the mighty
                Nile River forces approximately 300 cubic meters of water per second through a narrow 7-8 meter gap
                before plunging 45 meters into the Devil's Cauldron below.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-lg">
              <h3 className="text-lg font-bold text-safari-green mb-2">World-Class Boat Safaris</h3>
              <p className="text-gray-700">
                The boat cruise to the base of Murchison Falls ranks among East Africa's premier wildlife experiences.
                During the 3-hour journey, visitors encounter dense populations of hippos, massive Nile crocodiles,
                and elephants, giraffes, and buffaloes drinking along the riverbanks.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-lg">
              <h3 className="text-lg font-bold text-safari-green mb-2">Exceptional Game Viewing</h3>
              <p className="text-gray-700">
                The park's northern sector offers classic African savanna experiences with excellent wildlife
                densities. Game drives regularly yield sightings of lions, leopards, elephants, buffaloes, and
                Rothschild's giraffes (three-quarters of the world's population).
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-lg">
              <h3 className="text-lg font-bold text-safari-green mb-2">Albert Nile Delta Birding</h3>
              <p className="text-gray-700">
                The 20-kilometer downstream boat cruise to the Albert Delta provides unparalleled birding
                opportunities, particularly for spotting the prehistoric shoebill stork. This journey offers sightings
                of over 200 water bird species.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-lg">
              <h3 className="text-lg font-bold text-safari-green mb-2">Top of the Falls Hike</h3>
              <p className="text-gray-700">
                The guided hike to the top of Murchison Falls combines history, adventure, and spectacular scenery.
                Starting from Baker's Point, the 45-minute trek follows the Winston Churchill Trail used by the
                British statesman in 1907.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-lg">
              <h3 className="text-lg font-bold text-safari-green mb-2">Chimpanzee Trekking</h3>
              <p className="text-gray-700">
                The ancient Budongo Forest harbors over 800 chimpanzees across six habituated groups. Chimpanzee
                trekking experiences last 3-4 hours and provide one-hour encounters with our closest relatives in
                their natural habitat.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How to Get There */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-2xl font-bold text-safari-green mb-8 text-center">How to Get There</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="card">
              <div className="p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-car" className="w-6 h-6" />
                  By Road from Kampala
                </h3>
                <div className="space-y-3 text-gray-700">
                  <p><strong>Via Masindi (5-6 hours):</strong> 305 km through well-maintained highway. Most common route with clear signage.</p>
                  <p><strong>Paraa Ferry:</strong> Free ferry crossing connects southern and northern sectors. Operates during daylight hours.</p>
                  <p className="text-sm italic">Tarmac road to park gates. 4WD useful but not essential for main tracks.</p>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-plane" className="w-6 h-6" />
                  By Air
                </h3>
                <div className="space-y-3 text-gray-700">
                  <p><strong>Pakuba Airstrip:</strong> Daily scheduled flights from Entebbe (1 hour 15 minutes). Northern sector location.</p>
                  <p><strong>Bugungu Airstrip:</strong> Southern sector alternative near Budongo Forest.</p>
                  <p className="text-sm italic">Charter flights available. Ideal for time-limited safaris and luxury experiences.</p>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-route" className="w-6 h-6" />
                  Northern Circuit
                </h3>
                <div className="space-y-3 text-gray-700">
                  <p><strong>Kidepo Valley:</strong> 7-8 hours northeast. Combine two Uganda's most remote parks.</p>
                  <p><strong>Ziwa Rhino Sanctuary:</strong> En route from Kampala. Add rhino tracking to your journey.</p>
                  <p className="text-sm italic">Popular as part of northern Uganda wildlife circuit with multiple parks.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Activities & Experiences */}
      <section className="py-16 bg-safari-light">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-2xl font-bold text-safari-green mb-8 text-center">Activities & Experiences</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="card group">
              <div className="relative h-40 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-person-hiking" className="w-20 h-20 text-white" />
              </div>
              <div className="p-6 flex flex-col flex-grow">
                <h3 className="text-lg font-bold text-safari-green mb-2">Hike to Top of Murchison Falls</h3>
                <p className="text-gray-700 mb-4 flex-grow">
                  Challenging trail to the "Devil's Cauldron" where the mighty Nile explodes through a 7-meter gap with thunderous force. Spectacular views and photo opportunities.
                </p>
                <div className="space-y-2 mb-4 text-sm text-gray-600">
                  <p><FontAwesomeIcon icon="fa-clock" className="w-4 h-4 mr-2" />Duration: 1-2 hours</p>
                  <p><FontAwesomeIcon icon="fa-chart-simple" className="w-4 h-4 mr-2" />Difficulty: Strenuous</p>
                </div>
                <p className="text-safari-gold font-bold mb-3 text-center text-lg">Activity fees apply</p>
                <Link to="/activities/murchison-falls-hike" className="btn-primary w-full text-center block">
                  View Experience
                </Link>
              </div>
            </div>
            <div className="card group">
              <div className="relative h-40 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-car" className="w-20 h-20 text-white" />
              </div>
              <div className="p-6 flex flex-col flex-grow">
                <h3 className="text-lg font-bold text-safari-green mb-2">Wildlife Game Drives</h3>
                <p className="text-gray-700 mb-4 flex-grow">
                  Safari in the northern sector for Rothschild giraffes, lions, elephants, buffalos, and Uganda kob. Best wildlife viewing in Uganda.
                </p>
                <div className="space-y-2 mb-4 text-sm text-gray-600">
                  <p><FontAwesomeIcon icon="fa-clock" className="w-4 h-4 mr-2" />Duration: 3-5 hours</p>
                  <p><FontAwesomeIcon icon="fa-chart-simple" className="w-4 h-4 mr-2" />Difficulty: Easy</p>
                </div>
                <p className="text-safari-gold font-bold mb-3 text-center text-lg">From $60 per person</p>
                <button onClick={() => handleBooking('Wildlife Game Drives - Murchison Falls')} className="btn-primary w-full">
                  Book Experience
                </button>
              </div>
            </div>
            <div className="card group">
              <div className="relative h-40 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-ship" className="w-20 h-20 text-white" />
              </div>
              <div className="p-6 flex flex-col flex-grow">
                <h3 className="text-lg font-bold text-safari-green mb-2">Nile Boat Cruises</h3>
                <p className="text-gray-700 mb-4 flex-grow">
                  Cruise upstream to the falls base or downstream to the delta. See hippos, crocodiles, elephants drinking, and the rare shoebill stork.
                </p>
                <div className="space-y-2 mb-4 text-sm text-gray-600">
                  <p><FontAwesomeIcon icon="fa-clock" className="w-4 h-4 mr-2" />Duration: 2-3 hours</p>
                  <p><FontAwesomeIcon icon="fa-chart-simple" className="w-4 h-4 mr-2" />Difficulty: Easy</p>
                </div>
                <p className="text-safari-gold font-bold mb-3 text-center text-lg">Activity fees apply</p>
                <Link to="/activities/boat-safaris" className="btn-primary w-full text-center block">
                  View Experience
                </Link>
              </div>
            </div>
            <div className="card group">
              <div className="relative h-40 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-fish" className="w-20 h-20 text-white" />
              </div>
              <div className="p-6 flex flex-col flex-grow">
                <h3 className="text-lg font-bold text-safari-green mb-2">Sport Fishing on the Nile</h3>
                <p className="text-gray-700 mb-4 flex-grow">
                  Fish for giant Nile perch up to 100kg with expert guides. Catch-and-release or keep your trophy. Boats and equipment provided.
                </p>
                <div className="space-y-2 mb-4 text-sm text-gray-600">
                  <p><FontAwesomeIcon icon="fa-clock" className="w-4 h-4 mr-2" />Duration: 3-6 hours</p>
                  <p><FontAwesomeIcon icon="fa-chart-simple" className="w-4 h-4 mr-2" />Difficulty: Moderate</p>
                </div>
                <p className="text-safari-gold font-bold mb-3 text-center text-lg">Permit fees apply</p>
                <Link to="/activities/murchison-sport-fishing" className="btn-primary w-full text-center block">
                  View Experience
                </Link>
              </div>
            </div>
            <div className="card group">
              <div className="relative h-40 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-binoculars" className="w-20 h-20 text-white" />
              </div>
              <div className="p-6 flex flex-col flex-grow">
                <h3 className="text-lg font-bold text-safari-green mb-2">Birdwatching Safaris</h3>
                <p className="text-gray-700 mb-4 flex-grow">
                  Spot over 450 species including the elusive shoebill stork, Goliath heron, and papyrus specialists along the Nile banks.
                </p>
                <div className="space-y-2 mb-4 text-sm text-gray-600">
                  <p><FontAwesomeIcon icon="fa-clock" className="w-4 h-4 mr-2" />Duration: 3-5 hours</p>
                  <p><FontAwesomeIcon icon="fa-chart-simple" className="w-4 h-4 mr-2" />Difficulty: Easy to Moderate</p>
                </div>
                <p className="text-safari-gold font-bold mb-3 text-center text-lg">From $50 per person</p>
                <button onClick={() => handleBooking('Birdwatching Safaris - Murchison Falls')} className="btn-primary w-full">
                  Book Experience
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Travel Tips */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-2xl font-bold text-safari-green mb-8 text-center">Travel Tips</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="card">
              <div className="p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-calendar" className="w-6 h-6" />
                  Best Time to Visit
                </h3>
                <div className="space-y-3 text-gray-700">
                  <p><strong>Dry Seasons (December-February, June-September):</strong> Best for game viewing and easier road access. Animals congregate around water sources.</p>
                  <p><strong>Wet Seasons (March-May, October-November):</strong> Lush scenery, excellent birding including migratory species. Some tracks challenging.</p>
                  <p className="text-sm italic">Shoebill storks best seen January-March. Plan 2-3 days minimum.</p>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-suitcase" className="w-6 h-6" />
                  What to Pack
                </h3>
                <div className="space-y-2 text-gray-700">
                  <p><strong>Essential:</strong> Neutral safari clothing, sturdy hiking boots, wide-brim hat, sunscreen, binoculars</p>
                  <p><strong>Recommended:</strong> Camera with telephoto lens, insect repellent, light rain jacket, refillable water bottle</p>
                  <p><strong>For fishing:</strong> Most equipment provided, but bring personal gear if preferred</p>
                  <p className="text-sm italic">Temperatures hot and humid. Bring lightweight, breathable clothing.</p>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-lightbulb" className="w-6 h-6" />
                  Insider Tips
                </h3>
                <div className="space-y-2 text-gray-700">
                  <p><strong>Ferry Crossing:</strong> Arrive early to avoid long waits, especially during peak season.</p>
                  <p><strong>Boat Cruise:</strong> Afternoon departures offer better light for photography. Book tickets in advance.</p>
                  <p><strong>Shoebill Viewing:</strong> Delta cruise has best success rates. Early morning optimal.</p>
                  <p><strong>Top of Falls Hike:</strong> Combine with boat cruise for full waterfall experience (different perspectives).</p>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-coins" className="w-6 h-6" />
                  Budgeting Tips
                </h3>
                <div className="space-y-2 text-gray-700">
                  <p><strong>Park Entry:</strong> Foreign adults $40/day. Valid 24 hours from entry.</p>
                  <p><strong>Accommodation:</strong> Budget camping to luxury lodges ($30-$500+ per night).</p>
                  <p><strong>Activities:</strong> Boat cruise $30, game drives $60+, fishing $80+, chimp tracking extra.</p>
                  <p><strong>En Route Stop:</strong> Add Ziwa Rhino Sanctuary ($50 for rhino tracking) on journey from Kampala.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Where to Stay */}
      <section className="py-16 bg-safari-light">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-2xl font-bold text-safari-green mb-6 text-center">Where to Stay</h2>
          <p className="text-center text-gray-700 mb-12 max-w-3xl mx-auto">
            Accommodation options range from riverside campsites to luxury safari lodges, with locations in both northern and southern sectors.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full bg-white rounded-lg shadow-lg">
              <thead className="bg-safari-green text-white">
                <tr>
                  <th className="px-6 py-4 text-left">Category</th>
                  <th className="px-6 py-4 text-left">Accommodation Name</th>
                  <th className="px-6 py-4 text-left">Location</th>
                  <th className="px-6 py-4 text-left">Features</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-bold text-safari-green">Luxury</td>
                  <td className="px-6 py-4">Paraa Safari Lodge</td>
                  <td className="px-6 py-4">North bank, Paraa</td>
                  <td className="px-6 py-4">Premier rooms, infinity pool, Nile views, fine dining, spa</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-bold text-safari-green">Luxury</td>
                  <td className="px-6 py-4">Chobe Safari Lodge</td>
                  <td className="px-6 py-4">Murchison River Nile</td>
                  <td className="px-6 py-4">Luxury cottages, riverside location, game viewing platform</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-bold text-safari-green">Mid-Range</td>
                  <td className="px-6 py-4">Pakuba Safari Lodge</td>
                  <td className="px-6 py-4">Northern sector</td>
                  <td className="px-6 py-4">Comfortable tents, stunning views, good value, pool</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-bold text-safari-green">Mid-Range</td>
                  <td className="px-6 py-4">Fort Murchison</td>
                  <td className="px-6 py-4">Fajao Gorge</td>
                  <td className="px-6 py-4">Safari tents, bar, restaurant, escarpment views</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-bold text-safari-green">Budget</td>
                  <td className="px-6 py-4">Red Chilli Rest Camp</td>
                  <td className="px-6 py-4">Paraa area</td>
                  <td className="px-6 py-4">Budget bandas, camping, backpacker friendly, communal facilities</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-bold text-safari-green">Budget</td>
                  <td className="px-6 py-4">UWA Bandas & Campsites</td>
                  <td className="px-6 py-4">Multiple park locations</td>
                  <td className="px-6 py-4">Basic self-catering, camping available, very affordable</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-2xl font-bold text-safari-green mb-8 text-center">Frequently Asked Questions</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-6">
              <div className="border-l-4 border-safari-gold pl-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">How difficult is the hike to the top of the falls?</h3>
                <p className="text-gray-700">
                  Moderate to strenuous. The 45-minute trek involves steep sections and rocky terrain. Suitable for reasonably fit visitors. Views from the top are spectacular and worth the effort.
                </p>
              </div>
              <div className="border-l-4 border-safari-gold pl-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">What are the chances of seeing a shoebill stork?</h3>
                <p className="text-gray-700">
                  On the delta cruise, around 70-80% during January-March (best season). Downstream trips specifically target shoebill habitats. Bring binoculars and patience.
                </p>
              </div>
              <div className="border-l-4 border-safari-gold pl-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Do I need to book boat cruises in advance?</h3>
                <p className="text-gray-700">
                  Highly recommended during peak season (June-September, December-February). Walk-in tickets possible but departures may be full. Morning and afternoon slots available.
                </p>
              </div>
              <div className="border-l-4 border-safari-gold pl-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Can I see all Big Five here?</h3>
                <p className="text-gray-700">
                  Four of the Big Five: elephants, lions, leopards, and buffaloes. Rhinos were poached to extinction but there's a reintroduction program. Visit nearby Ziwa Sanctuary for rhinos.
                </p>
              </div>
            </div>
            <div className="space-y-6">
              <div className="border-l-4 border-safari-gold pl-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">How long does the Paraa ferry crossing take?</h3>
                <p className="text-gray-700">
                  5-10 minutes for the actual crossing. Wait times vary (15 minutes to 1+ hour during peak times). Free crossing operates during daylight hours only (approximately 7am-7pm).
                </p>
              </div>
              <div className="border-l-4 border-safari-gold pl-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Is Murchison Falls suitable for families with children?</h3>
                <p className="text-gray-700">
                  Yes! Boat cruises, game drives, and less strenuous activities are family-friendly. The falls hike may be challenging for young children. Many lodges offer family rooms and child activities.
                </p>
              </div>
              <div className="border-l-4 border-safari-gold pl-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">What's the difference between upstream and delta boat cruises?</h3>
                <p className="text-gray-700">
                  Upstream (to falls base): 3 hours, massive hippo pods, crocodiles, waterfall views. Delta cruise: 4-5 hours, shoebill storks, more bird species, different scenery. Both excellent.
                </p>
              </div>
              <div className="border-l-4 border-safari-gold pl-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Should I stay in northern or southern sector?</h3>
                <p className="text-gray-700">
                  Northern sector: Better game viewing, more accommodation options, Paraa activities hub. Southern sector: Budongo Forest chimps, quieter, easier Kampala access. Many visit both.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-safari-green text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Experience the Power of the Nile
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Witness the incredible force of Murchison Falls and discover Uganda's premier safari destination.
            From thundering waterfalls to exceptional wildlife viewing, Murchison Falls offers an authentic African
            adventure that will leave you breathless.
          </p>
          <Link
            to="/plan-your-trip"
            className="btn-primary text-lg inline-block"
          >
            Plan Your Murchison Falls Adventure
          </Link>
        </div>
      </section>

      <BookingModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        experience={selectedExperience}
      />
    </div>
  )
}